// Banners da Home (B5a): datas "mostrar de / até" e o movimento automático, por cima do
// carrossel do DS (ds-carrossel.js, circular e com pontinhos). QUEM decide é o núcleo
// (ds-banners-core.js, testado); aqui só DOM e eventos.
// Travas do movimento (decisão do dono 2026-09-28): só com o topo na tela e a aba visível; pausa
// com o mouse em cima ou o dedo parado no banner; para DE VEZ quando a pessoa mexe (seta,
// pontinho, arrastar PARA O LADO, rolagem lateral do trackpad, foco do teclado); botão de pausa;
// nunca com "reduzir movimento" nem no editor.
const core = await window.ArpineDS.importar("bannersCore");
const carrossel = await window.ArpineDS.importar("carrossel");

const reduzMovimento = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
const noEditor = () => Boolean(window.Shopify?.designMode);
const ligados = new WeakMap(); // raiz → desligar()

// Datas: o Liquid já escondeu pela hora dele (que pode estar parada no cache); aqui vale o
// relógio de quem visita. No editor nada some — o dono precisa ver o banner agendado — e cada um
// ganha a etiqueta ("agendado: …" ou "data inválida").
function aplicarDatas(raiz) {
  const agora = Date.now();
  const editor = noEditor();
  const banners = [...raiz.querySelectorAll("[data-ds-banner]")];
  for (const b of banners) {
    const janela = { inicio: b.dataset.inicio, fim: b.dataset.fim };
    b.hidden = editor ? false : !core.dentroDaJanela(janela, agora);
    const etiqueta = b.querySelector("[data-ds-banner-etiqueta]");
    if (etiqueta) {
      etiqueta.textContent = core.etiquetaDoBanner(janela);
      etiqueta.hidden = !etiqueta.textContent;
    }
  }
  const visiveis = banners.filter((b) => !b.hidden);
  visiveis.forEach((b, i) => b.setAttribute("aria-label", `${i + 1} de ${visiveis.length}`));
  // O Liquid escolheu o primeiro visível pela hora dele: se o navegador discordou, a foto do
  // primeiro de verdade não pode esperar a vez dela.
  visiveis[0]?.querySelector("img")?.setAttribute("loading", "eager");
  raiz.hidden = !editor && visiveis.length === 0;
  return visiveis;
}

function iniciar(raiz) {
  ligados.get(raiz)?.();
  const visiveis = aplicarDatas(raiz);
  const caixa = raiz.querySelector("[data-ds-carrossel]");
  const controle = carrossel.ligar(caixa);
  if (!controle) return;
  const lista = caixa.querySelector("[data-ds-carrossel-lista]");
  const controles = raiz.querySelector("[data-ds-banners-controles]");
  const pausa = raiz.querySelector("[data-ds-banners-pausa]");
  const status = raiz.querySelector("[data-ds-banners-status]");
  const intervalo = Math.max(4, Number(raiz.dataset.intervalo) || 7) * 1000;
  const reduz = reduzMovimento();
  const editor = noEditor();
  const estado = {
    visivel: false,
    abaVisivel: document.visibilityState === "visible",
    mouse: false, // mouse em cima
    dedo: false, // dedo encostado no banner
    interagiu: false,
    pausado: false,
  };

  controles.hidden = visiveis.length <= 1;
  pausa.hidden = reduz || editor; // nada a pausar

  const fim = new AbortController();
  const { signal } = fim;
  let relogio = null;
  const itens = () => [...lista.children].filter((el) => !el.hidden);
  const pode = () =>
    !editor &&
    core.deveRodar({ ...estado, sobre: estado.mouse || estado.dedo, total: controle.total(), reduzMovimento: reduz });

  function pintarPausa() {
    const parado = estado.pausado || estado.interagiu;
    pausa.dataset.estado = parado ? "parado" : "rodando";
    pausa.setAttribute("aria-label", parado ? "Continuar os banners" : "Pausar os banners");
  }

  // A foto do banner SEGUINTE ao atual começa a carregar antes da vez dela (o lazy não dispararia a
  // tempo numa rolagem programada) — só depois do load, para não disputar com a foto do primeiro.
  function prepararProximo(atual) {
    if (document.readyState !== "complete") return;
    const prox = itens()[core.proximoIndice(atual, controle.total())];
    prox?.querySelector("img")?.setAttribute("loading", "eager");
  }

  // O relógio recomeça a cada mudança: o banner seguinte fica o tempo cheio. `atual` vem de quem
  // acabou de mandar rolar (a rolagem suave ainda não chegou, então indice() ainda diria o antigo).
  function reiniciar(atual = controle.indice()) {
    clearTimeout(relogio);
    relogio = null;
    const rodando = pode();
    status.setAttribute("aria-live", rodando ? "off" : "polite"); // padrão APG de carrossel
    pintarPausa();
    if (!rodando) return;
    prepararProximo(atual);
    relogio = setTimeout(() => {
      const alvo = core.proximoIndice(controle.indice(), controle.total());
      controle.irPara(alvo);
      reiniciar(alvo);
    }, intervalo);
  }

  // Mexeu = para de vez (até recarregar ou apertar "Continuar").
  function parar() {
    if (estado.interagiu) return;
    estado.interagiu = true;
    reiniciar();
  }

  // Leitor de tela: rolar não muda o DOM, então a lista não teria o que anunciar. Uma região
  // escondida diz qual banner chegou — só é lida com o carrossel parado (aria-live "off" enquanto
  // roda, senão falaria a cada 7 s).
  let anunciado = controle.indice();
  let quadro = 0;
  function anunciar() {
    const i = controle.indice();
    if (i === anunciado) return;
    anunciado = i;
    const titulo = itens()[i]?.querySelector(".ds-banner__titulo")?.textContent.trim();
    status.textContent = `Banner ${i + 1} de ${controle.total()}${titulo ? `: ${titulo}` : ""}`;
  }
  lista.addEventListener(
    "scroll",
    () => {
      cancelAnimationFrame(quadro);
      quadro = requestAnimationFrame(anunciar);
    },
    { passive: true, signal }
  );

  const observador = new IntersectionObserver(
    (entradas) => {
      const e = entradas[entradas.length - 1]; // várias juntas: vale a mais nova
      // No celular DEITADO o banner é mais alto que a tela: a razão de interseção nunca chega a
      // 0,5 (core.topoVisivel cobre isso pela altura visível contra a altura da tela).
      const agora = core.topoVisivel({
        intersecta: e.isIntersecting,
        razao: e.intersectionRatio,
        alturaVisivel: e.intersectionRect.height,
        alturaTela: e.rootBounds?.height ?? window.innerHeight,
      });
      if (agora === estado.visivel) return; // cruzar outro limiar não reinicia o relógio
      estado.visivel = agora;
      reiniciar();
    },
    // Limiares finos: o callback tem que disparar quando a parte visível de um banner alto cruza
    // a metade da altura da TELA, não só quando a razão do próprio elemento cruza 0,5.
    { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5] }
  );
  observador.observe(raiz);

  document.addEventListener(
    "visibilitychange",
    () => {
      estado.abaVisivel = document.visibilityState === "visible";
      reiniciar();
    },
    { signal }
  );

  // Mouse em cima: pausa enquanto estiver.
  raiz.addEventListener(
    "pointerenter",
    (e) => {
      if (e.pointerType !== "mouse") return;
      estado.mouse = true;
      reiniciar();
    },
    { signal }
  );
  raiz.addEventListener(
    "pointerleave",
    (e) => {
      if (e.pointerType !== "mouse") return;
      estado.mouse = false;
      reiniciar();
    },
    { signal }
  );

  // Dedo no banner: pausa enquanto estiver encostado (como o mouse em cima). Arrastar PARA O LADO
  // é mexer no carrossel — para de vez. Rolar a página começando o gesto no banner NÃO conta: no
  // celular o banner ocupa meia tela e quase toda primeira rolagem começa nele.
  let toque = null;
  lista.addEventListener(
    "touchstart",
    (e) => {
      const t = e.touches[0];
      toque = { x: t.clientX, y: t.clientY, decidido: false };
      estado.dedo = true;
      reiniciar();
    },
    { passive: true, signal }
  );
  lista.addEventListener(
    "touchmove",
    (e) => {
      if (!toque || toque.decidido) return;
      const t = e.touches[0];
      const dx = t.clientX - toque.x;
      const dy = t.clientY - toque.y;
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 10) return; // tremida do dedo
      toque.decidido = true;
      if (Math.abs(dx) > Math.abs(dy)) parar();
    },
    { passive: true, signal }
  );
  const soltar = () => {
    toque = null;
    estado.dedo = false;
    reiniciar();
  };
  lista.addEventListener("touchend", soltar, { passive: true, signal });
  lista.addEventListener("touchcancel", soltar, { passive: true, signal });

  caixa.addEventListener(
    "click",
    (e) => {
      if (e.target.closest("[data-ds-carrossel-seta], [data-ds-carrossel-ponto]")) parar();
    },
    { signal }
  );
  // Roda do mouse: só a rolagem LATERAL (trackpad arrastando os banners) conta. Rolar a página
  // para baixo com o mouse em cima do banner não é mexer no carrossel.
  lista.addEventListener(
    "wheel",
    (e) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) parar();
    },
    { passive: true, signal }
  );
  // Foco do TECLADO entrando (padrão APG: quem navega por teclado quer ler). O foco que um clique
  // de mouse deixa no botão de pausa não conta — :focus-visible separa os dois.
  raiz.addEventListener(
    "focusin",
    (e) => {
      if (e.target.matches?.(":focus-visible")) parar();
    },
    { signal }
  );

  pausa.addEventListener(
    "click",
    () => {
      const parado = estado.pausado || estado.interagiu;
      estado.pausado = !parado;
      if (parado) estado.interagiu = false; // "Continuar" é o pedido explícito de voltar a rodar
      reiniciar();
    },
    { signal }
  );

  // Editor: selecionar um bloco leva ao banner dele.
  document.addEventListener(
    "shopify:block:select",
    (e) => {
      if (!raiz.contains(e.target)) return;
      const i = itens().indexOf(e.target.closest(".ds-banner") ?? e.target);
      if (i >= 0) controle.irPara(i);
    },
    { signal }
  );

  window.addEventListener("load", () => prepararProximo(controle.indice()), { once: true, signal });

  ligados.set(raiz, () => {
    fim.abort();
    observador.disconnect();
    clearTimeout(relogio);
    cancelAnimationFrame(quadro);
    ligados.delete(raiz);
  });
  reiniciar();
}

for (const raiz of document.querySelectorAll("[data-ds-banners]")) iniciar(raiz);

// Editor do tema: a seção é redesenhada a cada mudança de setting (o módulo não roda de novo).
document.addEventListener("shopify:section:load", (e) => {
  const raiz = e.target.querySelector?.("[data-ds-banners]");
  if (raiz) iniciar(raiz);
});
document.addEventListener("shopify:section:unload", (e) => {
  const raiz = e.target.querySelector?.("[data-ds-banners]");
  if (raiz) ligados.get(raiz)?.();
});
